import { useLayoutEffect, useRef, useState } from 'react';
import React from "react";
import { Box } from "@mui/material";
import { ExpandMore } from '@mui/icons-material';

const ReusableNodes = ({
    selected,
    children,
    isContentExpandable = true,
    defaultExpanded = true,
    backgroundColor,
    className = '',
    style: rootStyle,
    highlighted,
    focusMode,
    ...props
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const [headerAnchorY, setHeaderAnchorY] = useState(null);
    const rootRef = useRef(null);
    const headerRef = useRef(null);

    let nodeContent = null;
    let nodeHandles = null;
    let nodeHeader = null;
    let nodeWarning = null;
    let nodeError = null;

    React.Children.forEach(children, (child) => {
        if (!React.isValidElement(child)) return;

        switch (child.type) {
            case NodeHeader:
                nodeHeader = child.props.children;
                break;
            case NodeContent:
                nodeContent = child;
                break;
            case NodeHandles:
                nodeHandles = child;
                break;
            case NodeWarning:
                nodeWarning = child;
                break;
            case NodeError:
                nodeError = child;
                break;
            default:
                break;
        }
    });

    const contentBody = nodeContent?.props?.children;
    const hasContent = React.Children.count(contentBody) > 0;
    const canToggleContent = hasContent && isContentExpandable;

    const handlesBody = nodeHandles?.props?.children;
    const resolvedNodeHandles = typeof handlesBody === 'function'
        ? handlesBody({ headerAnchorY })
        : handlesBody;

    const resolvedBackgroundColor = String(backgroundColor || '#ffffff').trim() || '#ffffff';

    useLayoutEffect(() => {
        const updateHeaderAnchorY = () => {
            if (!rootRef.current) {
                return;
            }

            const rootCenterY = rootRef.current.offsetHeight / 2;

            if (!hasContent || !headerRef.current) {
                setHeaderAnchorY(rootCenterY);
                return;
            }

            // Use local offsets instead of viewport rect math so anchor does not drift with viewport transforms.
            const nextAnchorY = headerRef.current.offsetTop + (headerRef.current.offsetHeight / 2);
            setHeaderAnchorY(nextAnchorY);
        };

        updateHeaderAnchorY();
        const rafId = window.requestAnimationFrame(updateHeaderAnchorY);

        const resizeHandler = () => updateHeaderAnchorY();
        window.addEventListener('resize', resizeHandler);

        let observer;
        if (typeof ResizeObserver !== 'undefined' && rootRef.current) {
            observer = new ResizeObserver(updateHeaderAnchorY);
            observer.observe(rootRef.current);
            if (headerRef.current) {
                observer.observe(headerRef.current);
            }
        }

        return () => {
            window.cancelAnimationFrame(rafId);
            window.removeEventListener('resize', resizeHandler);
            if (observer) {
                observer.disconnect();
            }
        };
    }, [isExpanded, hasContent, selected, children]);

    return (
        <div
            ref={rootRef}
            {...props}
            className={`min-w-[220px] max-w-[320px] node-template group/node !bg-[var(--node-bg)] ${(selected || highlighted) ? 'node-template-selected' : ''} ${className}`.trim()}
            style={{
                ...rootStyle,
                '--node-bg': resolvedBackgroundColor,
                opacity: focusMode ? highlighted ? 1 : 0.5 : 1,
            }}
        >
            {nodeWarning && (
                <div
                    style={{
                        position: 'absolute',
                        top: '-15px',
                        right: '10%',
                        transform: 'translate(-50%, 0)',
                        zIndex: 1000,
                        textAlign: 'center',
                    }}
                >
                    {nodeWarning}
                </div>
            )}
            {nodeError && (
                <div
                    style={{
                        position: 'absolute',
                        top: '-15px',
                        right: '10%',
                        transform: 'translate(-50%, 0)',
                        zIndex: 1000,
                        textAlign: 'center',
                    }}
                >
                    {nodeError}
                </div>
            )}
            <Box>
                <div className="flex items-center justify-between gap-2">
                    <NodeHeader ref={headerRef} className="min-w-0 flex-1">
                        {nodeHeader}
                    </NodeHeader>

                    {canToggleContent && (
                        <button
                            type="button"
                            className="flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-700"
                            onClick={() => setIsExpanded((prev) => !prev)}
                            aria-label={isExpanded ? 'Collapse content' : 'Expand content'}
                            aria-expanded={isExpanded}
                        >
                            <ExpandMore className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : 'rotate-0'}`} />
                        </button>
                    )}
                </div>

                {canToggleContent && (
                    <div
                        className="grid transition-[grid-template-rows] duration-200 ease-in-out"
                        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
                    >
                        <div className="overflow-hidden">
                            <div
                                className={`transition-all duration-200 ease-in-out ${isExpanded ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'}`}
                            >
                                <NodeContent {...nodeContent.props}>
                                    {contentBody}
                                </NodeContent>
                            </div>
                        </div>
                    </div>
                )}

                {!canToggleContent && hasContent && (
                    <NodeContent {...nodeContent.props}>
                        {contentBody}
                    </NodeContent>
                )}
            </Box>

            {nodeHandles && (
                <NodeHandles {...nodeHandles.props}>
                    {resolvedNodeHandles}
                </NodeHandles>
            )}
        </div>
    )
}

export default ReusableNodes;

export const NodeHeader = React.forwardRef(({
    children,
    className = "",
    ...props
}, ref) => {
    return (
        <div ref={ref} className={`flex items-center gap-2 ${className}`.trim()} {...props}>
            {children}
        </div>
    );
});

NodeHeader.displayName = 'NodeHeader';

export const NodeContent = ({
    children,
    className = "",
    ...props
}) => {
    return (
        <div className={`flex flex-col mt-2 max-w-[320px] ${className}`.trim()} {...props}>
            {children}
        </div>
    );
};

export const NodeHandles = ({
    children,
    className = "",
    ...props
}) => {
    return (
        <div className={`${className}`} {...props}>
            {children}
        </div>
    );
};

export const NodeWarning = ({
    children,
    className = "",
    sx = {},
    ...props
}) => {
    return (
        <Box className={`${className}`} sx={sx} {...props}>
            {children}
        </Box>
    );
};

export const NodeError = ({
    children,
    className = "",
    sx = {},
    ...props
}) => {
    return (
        <Box className={`${className}`} sx={sx} {...props}>
            {children}
        </Box>
    );
};